import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, AfterViewInit, OnChanges, SimpleChanges, HostListener, ElementRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { TableColumn } from '../data-table.component';
import { ColumnFilter, FilterOperator, getAvailableOperators, getDefaultOperator, FILTER_OPERATOR_LABELS, SortDirection } from '../filter-types';

@Component({
  selector: 'app-column-filter-dropdown',
  templateUrl: './column-filter-dropdown.component.html',
  styleUrls: ['./column-filter-dropdown.component.scss']
})
export class ColumnFilterDropdownComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input() column!: TableColumn;
  @Input() currentFilter: ColumnFilter | null = null;
  @Input() uniqueValues: any[] = [];
  @Input() data: any[] = [];
  @Input() triggerElement: HTMLElement | null = null;

  @Output() filterApply = new EventEmitter<ColumnFilter | null>();
  @Output() filterClear = new EventEmitter<void>();
  @Output() sortApply = new EventEmitter<SortDirection>();
  @Output() closed = new EventEmitter<void>();

  @ViewChild('dropdownMenu', { static: false }) dropdownMenu!: ElementRef;
  @ViewChild('dropdownContainer', { static: false }) dropdownContainer!: ElementRef;

  constructor(private cdr: ChangeDetectorRef) {}

  isOpen: boolean = false;
  availableOperators: FilterOperator[] = [];
  selectedOperator: FilterOperator | null = null;
  filterValues: any[] = [];
  tempFilterValues: any[] = [];
  sortDirection: SortDirection = null;

  // For different input types
  textInputValue: string = '';
  numberInputValue: number | null = null;
  numberMinValue: number | null = null;
  numberMaxValue: number | null = null;
  dateInputValue: Date | null = null;
  dateFromValue: Date | null = null;
  dateToValue: Date | null = null;

  ngOnInit(): void {
    this.availableOperators = getAvailableOperators(this.column.type || 'text');
    this.isOpen = true; // Open by default when component is created
    
    if (this.currentFilter) {
      this.selectedOperator = this.currentFilter.operator;
      this.filterValues = [...this.currentFilter.values];
      this.tempFilterValues = [...this.currentFilter.values];
      this.sortDirection = this.currentFilter.sortDirection || null;
      this.initializeInputValues();
    } else {
      // Set default operator
      this.selectedOperator = getDefaultOperator(this.column.type || 'text');
    }
  }

  ngAfterViewInit(): void {
    // Calculate position based on trigger element
    this.calculatePosition();
    // Add scroll and resize listeners
    this.addEventListeners();
  }

  ngOnDestroy(): void {
    // Remove event listeners
    this.removeEventListeners();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Recalculate position when triggerElement changes
    if (changes['triggerElement'] && !changes['triggerElement'].firstChange && this.dropdownContainer) {
      setTimeout(() => {
        this.calculatePosition();
      }, 0);
    }
  }

  private scrollHandler = () => {
    if (this.isOpen && this.triggerElement) {
      this.calculatePosition();
    }
  };

  private resizeHandler = () => {
    if (this.isOpen && this.triggerElement) {
      this.calculatePosition();
    }
  };

  private addEventListeners(): void {
    window.addEventListener('scroll', this.scrollHandler, true);
    window.addEventListener('resize', this.resizeHandler);
  }

  private removeEventListeners(): void {
    window.removeEventListener('scroll', this.scrollHandler, true);
    window.removeEventListener('resize', this.resizeHandler);
  }

  private calculatePosition(): void {
    if (!this.triggerElement || !this.dropdownContainer) {
      return;
    }

    const triggerRect = this.triggerElement.getBoundingClientRect();
    const container = this.dropdownContainer.nativeElement;
    
    // Set position using fixed positioning (relative to viewport, not document)
    container.style.position = 'fixed';
    container.style.zIndex = '10000';
    
    // Adjust if dropdown goes off screen
    setTimeout(() => {
      const dropdownRect = container.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const dropdownWidth = dropdownRect.width;
      const dropdownHeight = dropdownRect.height;
      
      // Position dropdown to align right edge with trigger button right edge
      // Use getBoundingClientRect() which gives viewport-relative coordinates for fixed positioning
      let leftPosition = triggerRect.right - dropdownWidth;
      
      // Adjust horizontal position if dropdown goes off screen
      if (leftPosition < 10) {
        leftPosition = 10;
      } else if (triggerRect.right > viewportWidth - 10) {
        leftPosition = Math.max(10, viewportWidth - dropdownWidth - 10);
      }
      
      container.style.left = `${leftPosition}px`;
      
      // Calculate vertical position
      let topPosition = triggerRect.bottom + 4;
      
      // Adjust vertical position if dropdown goes off screen
      if (topPosition + dropdownHeight > viewportHeight) {
        // If dropdown goes below viewport, show it above the trigger button
        topPosition = triggerRect.top - dropdownHeight - 4;
        // If still off screen, position at top of viewport
        if (topPosition < 10) {
          topPosition = 10;
        }
      }
      
      container.style.top = `${topPosition}px`;
    }, 0);
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
  }

  closeDropdown(): void {
    this.isOpen = false;
    this.closed.emit();
  }

  onOperatorChange(operator: FilterOperator): void {
    this.selectedOperator = operator;
    this.tempFilterValues = [];
    this.clearInputValues();
  }

  // Sort handlers
  onSortAscending(): void {
    this.sortDirection = 'asc';
    this.sortApply.emit('asc');
  }

  onSortDescending(): void {
    this.sortDirection = 'desc';
    this.sortApply.emit('desc');
  }

  onSortClear(): void {
    this.sortDirection = null;
    this.sortApply.emit(null);
  }

  // Filter value handlers
  onTextInputChange(value: string): void {
    this.textInputValue = value;
  }

  onNumberInputChange(value: string): void {
    this.numberInputValue = value ? Number(value) : null;
  }

  onNumberMinChange(value: string): void {
    this.numberMinValue = value ? Number(value) : null;
  }

  onNumberMaxChange(value: string): void {
    this.numberMaxValue = value ? Number(value) : null;
  }

  onDateInputChange(value: string): void {
    this.dateInputValue = value ? new Date(value) : null;
  }

  onDateFromChange(value: string): void {
    this.dateFromValue = value ? new Date(value) : null;
  }

  onDateToChange(value: string): void {
    this.dateToValue = value ? new Date(value) : null;
  }

  onMultiSelectChange(values: any[]): void {
    this.tempFilterValues = values;
  }

  // Apply filter
  onApplyFilter(): void {
    const values = this.getFilterValues();
    
    console.log('onApplyFilter - values:', values);
    console.log('onApplyFilter - selectedOperator:', this.selectedOperator);
    console.log('onApplyFilter - sortDirection:', this.sortDirection);
    console.log('onApplyFilter - column type:', this.column.type);
    console.log('onApplyFilter - textInputValue:', this.textInputValue);
    console.log('onApplyFilter - numberInputValue:', this.numberInputValue);
    console.log('onApplyFilter - dateInputValue:', this.dateInputValue);
    console.log('onApplyFilter - tempFilterValues:', this.tempFilterValues);
    
    // Check if we have filter values or sort direction
    const hasFilterValues = values.length > 0 || 
                           this.selectedOperator === 'today' || 
                           this.selectedOperator === 'thisWeek' || 
                           this.selectedOperator === 'thisMonth';
    const hasSort = this.sortDirection !== null;
    
    // If no filter values and no sort, clear the filter
    if (!hasFilterValues && !hasSort) {
      console.log('onApplyFilter - Clearing filter (no values, no sort)');
      this.filterClear.emit();
      return;
    }

    const filter: ColumnFilter = {
      field: this.column.field,
      operator: this.selectedOperator!,
      values: values,
      sortDirection: this.sortDirection
    };

    console.log('onApplyFilter - Emitting filter:', filter);
    this.filterValues = [...values];
    this.filterApply.emit(filter);
    this.closeDropdown();
  }

  // Clear filter
  onClearFilter(): void {
    this.selectedOperator = getDefaultOperator(this.column.type || 'text');
    this.filterValues = [];
    this.tempFilterValues = [];
    this.sortDirection = null;
    this.clearInputValues();
    this.filterClear.emit();
    this.closeDropdown();
  }

  private getFilterValues(): any[] {
    if (!this.selectedOperator) return [];
    
    const operator = this.selectedOperator as string;
    
    if (operator === 'between') {
      if (this.column.type === 'number') {
        if (this.numberMinValue != null || this.numberMaxValue != null) {
          return [this.numberMinValue, this.numberMaxValue];
        }
      } else if (this.column.type === 'date') {
        if (this.dateFromValue || this.dateToValue) {
          return [this.dateFromValue, this.dateToValue];
        }
      }
    } else if (operator === 'today' || operator === 'thisWeek' || operator === 'thisMonth') {
      return [operator];
    } else if (this.column.type === 'badge' || operator === 'isOneOf' || operator === 'isNotOneOf') {
      if (this.tempFilterValues && this.tempFilterValues.length > 0) {
        return this.tempFilterValues;
      }
    } else if (this.column.type === 'text' || this.column.type === 'link') {
      if (this.textInputValue && this.textInputValue.trim() !== '') {
        return [this.textInputValue.trim()];
      }
    } else if (this.column.type === 'number') {
      if (this.numberInputValue != null) {
        return [this.numberInputValue];
      }
    } else if (this.column.type === 'date') {
      if (this.dateInputValue) {
        return [this.dateInputValue];
      }
    }

    return [];
  }

  private initializeInputValues(): void {
    if (this.filterValues.length === 0 || !this.selectedOperator) return;

    const operator = this.selectedOperator as string;

    if (operator === 'between') {
      if (this.column.type === 'number') {
        this.numberMinValue = this.filterValues[0] != null ? Number(this.filterValues[0]) : null;
        this.numberMaxValue = this.filterValues[1] != null ? Number(this.filterValues[1]) : null;
      } else if (this.column.type === 'date') {
        this.dateFromValue = this.filterValues[0] ? new Date(this.filterValues[0]) : null;
        this.dateToValue = this.filterValues[1] ? new Date(this.filterValues[1]) : null;
      }
    } else if (operator === 'isOneOf' || operator === 'isNotOneOf' || this.column.type === 'badge') {
      this.tempFilterValues = [...this.filterValues];
    } else if (this.column.type === 'text' || this.column.type === 'link') {
      this.textInputValue = this.filterValues[0] || '';
    } else if (this.column.type === 'number') {
      this.numberInputValue = this.filterValues[0] != null ? Number(this.filterValues[0]) : null;
    } else if (this.column.type === 'date') {
      this.dateInputValue = this.filterValues[0] ? new Date(this.filterValues[0]) : null;
    }
  }

  private clearInputValues(): void {
    this.textInputValue = '';
    this.numberInputValue = null;
    this.numberMinValue = null;
    this.numberMaxValue = null;
    this.dateInputValue = null;
    this.dateFromValue = null;
    this.dateToValue = null;
  }

  getOperatorLabel(operator: FilterOperator): string {
    return FILTER_OPERATOR_LABELS[operator] || operator;
  }

  needsMultiSelect(): boolean {
    if (!this.selectedOperator) return false;
    const operator = this.selectedOperator as string;
    return operator === 'isOneOf' || operator === 'isNotOneOf' || 
           (this.column.type === 'badge' && (operator === 'isOneOf' || operator === 'isNotOneOf'));
  }

  needsSingleInput(): boolean {
    if (!this.selectedOperator) return false;
    const operator = this.selectedOperator as string;
    return !this.needsMultiSelect() && operator !== 'between' && 
           operator !== 'today' && operator !== 'thisWeek' && 
           operator !== 'thisMonth';
  }

  needsRangeInput(): boolean {
    if (!this.selectedOperator) return false;
    return (this.selectedOperator as string) === 'between';
  }

  needsDatePreset(): boolean {
    if (!this.selectedOperator) return false;
    const operator = this.selectedOperator as string;
    return operator === 'today' || operator === 'thisWeek' || 
           operator === 'thisMonth';
  }

  getDateInputFormat(): string {
    return 'yyyy-MM-dd';
  }

  formatDateForInput(date: Date | null): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (this.isOpen && this.dropdownMenu && !this.dropdownMenu.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  @HostListener('keydown.escape')
  onEscapeKey(): void {
    if (this.isOpen) {
      this.closeDropdown();
    }
  }
}

